import React from 'react';
import { Helmet } from "react-helmet";

const webSiteSchema = () => {
  return JSON.stringify({
    "@type": "WebSite",
    "@id": "https://www.cryptowaves.app/#website",
    "url": "https://www.cryptowaves.app/",
    "name": "CryptoWaves.App",
    "inLanguage": "en-US",
    "description": "Crypto Market Relative Strength Index (RSI) Scanner, Tracker and Alerting Service | CryptoWaves.App",
    "publisher": {
        "@id": "https://www.cryptowaves.app/#organization"
    }
  })
}

const webPageSchema = (name, description, title, text) => {
  return JSON.stringify({
    "@context": "http://schema.org",
    "@type": "WebPage",
    "name": name,
    "description": description,
    "mainEntity": {
      "@type": "Question",
      "name": title,
      "answerCount": 1,
      "upvoteCount": 1,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": text,
        "upvoteCount": 1
      }
    },
    "publisher": {
      "@id": "https://www.cryptowaves.app/#organization"
    }
  })
}

const webApplication = () => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "CryptoWaves.App - Crypto Market Relative Strength Index (RSI) Scanner, Tracker and Alert Notifications",
    "operatingSystem": "all",
    "applicationCategory": "FinanceApplication",
    "url": "https://www.cryptowaves.app",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "1"
    },
    "offers": {
      "@type": "Offer",
      "price": "0"
    }
  })
}

const Seo = {
  landing: () => {
    return (
      <Helmet>
        <title>{`CryptoWaves.App - Crypto Market RSI Scanner & Tracker`}</title>
        <meta name="description" content={`Track Crypto Market Relative Strength Index (RSI). Get Personalized RSI Alerts On Your Email & Telegram. Know What Coins To Buy or Sell Next. Made For Crypto Traders and Investors.`}></meta>
        <script type="application/ld+json">{webSiteSchema()}</script>
        <meta property="og:url" content={`https://www.cryptowaves.app`}></meta>
        <meta property="og:type" content="website"></meta>
        <meta property="og:title" content={`CryptoWaves.App - Crypto Market RSI Scanner & Tracker`}></meta>
        <meta property="og:description" content={`Track Crypto Market Relative Strength Index (RSI). Get Personalized RSI Alerts On Your Email & Telegram. Know What Coins To Buy or Sell Next. Made For Crypto Traders and Investors.`}></meta>
        <meta property="og:image" content="https://cryptowaves-app.s3.us-east-2.amazonaws.com/heatmap.png"></meta>
        <meta name="twitter:site" content="@CryptoWavesApp"></meta>
        <meta name="twitter:card" content="summary_large_image"></meta>
        <meta name="twitter:title" content={`CryptoWaves.App 🌊 RSI Crypto 🚨 Alerts`}></meta>
        <meta name="twitter:description" content={`Know What Coins To Buy or Sell Next 🚀 Get Crypto Market RSI 🚨 Alerts On Your Email & Telegram. Join +18K Data-Driven Traders and Investors Today!`}></meta>
        <meta name="twitter:image" content="https://cryptowaves-app.s3.us-east-2.amazonaws.com/heatmap.png"></meta>
        <meta name="twitter:image:alt" content="CryptoWaves.App 🌊 Crypto Market RSI Heatmap" />
      </Helmet>
    )
  },
  coinRSI: (coin) => {
    return (
      <Helmet>
        <title>{coin.coin} ({coin.name}) RSI Live, Price, Moves, Charts And Alert Notifications | CryptoWaves.app</title>
        <meta name="description" content={`Track ${coin.coin} (${coin.name}) Relative Strength Index (RSI) Live, Price, Moves, Alerts, Charts on CryptoWaves.app. Get ${coin.coin} (${coin.name}) RSI Alert Notifications on Your Email & Telegram.`}></meta>
        <link rel="canonical" href={`https://www.cryptowaves.app/relative-strength-index/${coin.coin}`} />
        <script type="application/ld+json">{webSiteSchema()}</script>
        <script type="application/ld+json">{webPageSchema(
          `${coin.coin} (${coin.name}) RSI Live, Price, Moves, Charts And Alert Notifications | CryptoWaves.app`, 
          `Track ${coin.coin} (${coin.name}) Relative Strength Index (RSI) Live, Price, Moves, Alerts, Charts on CryptoWaves.app. Get ${coin.coin} (${coin.name}) RSI Alert Notifications on Your Email & Telegram.`,
          `What is ${coin.coin} (${coin.name}) Relative Strength Index (RSI) Strategy?`,
          `${coin.name} RSI is a very simple yet powerful indicator to find best time to entry or exit ${coin.name} trade on a mid-term or long-term interval. ${coin.name} RSI oscillates between 0 and 100. The most common RSI reading is that a particular crypto asset is "overbought" when the RSI line crosses the 70 level and an "oversold" when the RSI line crosses 30. That interpretation is based on the idea that if the ${coin.name} has been oversold (or overbought) it's therefore has a high probability of rebounding. Some traders use different range pairs like 80/20 (that is pretty common to see on crypto market) for more strong price signal identification and avoiding false RSI alerts. Truth be told it is very rare that the market can maintain it’s current momentum by the time the RSI indicator is signalling above 80 or below 20. During trends, the ${coin.name} RSI readings may fall into a band or range. During an uptrend on a crypto market, the ${coin.name} RSI tends to stay above 30 and will frequently hit 70. During a downtrend, it is rare to see the ${coin.name} RSI exceed 70, and the indicator frequently hits 30 or below. Traders and investors must be aware that on a bull market, RSI for most crypto coins often jumps between 40 and 90 (For example DOGE and ETC were both way over 80 during the last crypto market top), with levels 40 and 50 acting as a support range. For a bear market, traders can see RSI most often belongs the range from 20 to 60, with levels 55 and 60 acting as the hightest price ceiling. CryptoWaves.app helps traders and investors track ${coin.name} relative strength index (RSI) indicator in real time, receive ${coin.name} price alerts and RSI signal notifications straight to their email.`
          )}</script>
        <script type="application/ld+json">{webApplication()}</script>
        <meta property="og:url" content={`https://www.cryptowaves.app/relative-strength-index/${coin.coin}`}></meta>
        <meta property="og:type" content="website"></meta>
        <meta property="og:title" content={`CryptoWaves.app - ${coin.coin} (${coin.name}) RSI Live, Price, Moves, Charts And Alert Notifications`}></meta>
        <meta property="og:description" content={`Track ${coin.coin} (${coin.name}) Relative Strength Index (RSI) Live, Price, Moves, Alerts, Charts on CryptoWaves.app. Get ${coin.coin} (${coin.name}) RSI Alert Notifications on Your Email & Telegram.`}></meta>
        <meta property="og:image" content="https://cryptowaves-app.s3.us-east-2.amazonaws.com/heatmap.png"></meta>
        <meta name="twitter:card" content="summary_large_image"></meta>
        <meta name="twitter:site" content="@CryptoWavesApp"></meta>
        <meta name="twitter:title" content={`CryptoWaves.app - ${coin.coin} (${coin.name}) RSI Live, Price, Moves, Charts And Alert Notifications`}></meta>
        <meta name="twitter:description" content={`Track ${coin.coin} (${coin.name}) Relative Strength Index (RSI) Live, Price, Moves, Alerts, Charts on CryptoWaves.app. Get ${coin.coin} (${coin.name}) RSI Alert Notifications on Your Email & Telegram.`}></meta>
        <meta name="twitter:image" content="https://cryptowaves-app.s3.us-east-2.amazonaws.com/heatmap.png"></meta>
      </Helmet>
    )
  },
  toSlug: (string) => {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
    const p = new RegExp(a.split('').join('|'), 'g')
  
    return string.toString().toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '') // Trim - from end of text
  }
}

export default Seo;